import React from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import RepeatIcon from '@atlaskit/icon-lab/core/repeat';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ClockIcon from '@atlaskit/icon/core/clock';
import WarningOutlineIcon from '@atlaskit/icon-lab/core/warning-outline';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useRecurWorkItemState } from '@atlassian/jira-recur-work-item/src/controllers/index.tsx';
import { isRecurOnCompleteToggleRule } from '@atlassian/jira-recur-work-item/src/ui/utils.tsx';
import { messages } from './messages.tsx';

const RecurringWorkItemIcon = ({ testId }: { testId?: string }) => {
	const { issueProperties, isUpdatingRule } = useRecurWorkItemState();

	if ((!issueProperties && !isUpdatingRule) || isRecurOnCompleteToggleRule(issueProperties)) {
		return null;
	}

	return <RepeatIcon label="" testId={testId} />;
};

export type ColoredDueDateProps = {
	dateString: string;
	pastDue: boolean;
	dueToday: boolean;
	isShortFormat?: boolean;
	maybeShowRecurIcon?: boolean;
	calendarIconTestId?: string;
	clockIconTestId?: string;
	recurIconTestId?: string;
	tooltipTestId?: string;
	warningIconTestId?: string;
};

export const ColoredDueDate = ({
	dateString,
	pastDue,
	dueToday,
	warningIconTestId,
	clockIconTestId,
	calendarIconTestId,
	tooltipTestId,
	isShortFormat = false,
	maybeShowRecurIcon = false,
	recurIconTestId,
}: ColoredDueDateProps): React.JSX.Element => {
	const locale = useLocale();
	const localeString = locale.replace('_', '-');
	const formatter = new Intl.DateTimeFormat(localeString, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		timeZone: 'UTC',
	});
	const parts = formatter.formatToParts(new Date(dateString));

	const formatDateParts = ({
		dateParts,
		includeYear = true,
	}: {
		dateParts: Intl.DateTimeFormatPart[];
		includeYear?: boolean;
	}) => {
		let result = '';

		dateParts.forEach((part, index) => {
			const isYearLiteralInShortFormat =
				part.type === 'literal' &&
				(dateParts[index + 1]?.type === 'year' || dateParts[index - 1]?.type === 'year') &&
				!includeYear;

			if (
				(part.type === 'year' && includeYear) ||
				(part.type !== 'year' && !isYearLiteralInShortFormat)
			) {
				result += part.value;
			}
		});

		return result;
	};

	const displayDate = isShortFormat
		? formatDateParts({ dateParts: parts, includeYear: false })
		: formatDateParts({ dateParts: parts });
	const tooltipDate = formatDateParts({ dateParts: parts, includeYear: true });

	const dueMessage = pastDue ? (
		<FormattedMessage {...messages.overdue} values={{ date: tooltipDate }} />
	) : (
		<FormattedMessage {...messages.due} values={{ date: tooltipDate }} />
	);

	const { formatMessage } = useIntl();
	const iconLabel = pastDue
		? formatMessage(messages.overdue, { date: tooltipDate })
		: formatMessage(messages.due, { date: tooltipDate });

	return (
		<Tooltip content={dueMessage} position="top" testId={tooltipTestId}>
			<Box
				xcss={[
					styles.dateTag,
					styles.flexContainer,
					pastDue ? styles.overdue : undefined,
					dueToday ? styles.dueToday : undefined,
					!pastDue && !dueToday ? styles.normalDate : undefined,
				]}
				testId="coloured-due-date.ui.colored-due-date-container"
				tabIndex={0}
				role="button"
				aria-label={iconLabel}
			>
				<Box xcss={styles.dueDateIcon} testId="coloured-due-date.ui.tooltip-container">
					{pastDue && (
						<WarningOutlineIcon
							LEGACY_size="medium"
							label={iconLabel}
							color={token('color.text.danger')}
							testId={warningIconTestId}
						/>
					)}
					{dueToday && (
						<ClockIcon
							LEGACY_size="medium"
							label={iconLabel}
							color={token('color.text.warning')}
							testId={clockIconTestId}
						/>
					)}
					{!dueToday && !pastDue && (
						<CalendarIcon
							label={iconLabel}
							color={token('color.text')}
							testId={calendarIconTestId}
						/>
					)}
				</Box>
				<span aria-hidden="true">{displayDate}</span>
				{maybeShowRecurIcon && <RecurringWorkItemIcon testId={recurIconTestId} />}
			</Box>
		</Tooltip>
	);
};

const styles = {
	dateTag: xcss({
		paddingLeft: 'space.075',
		paddingRight: 'space.075',
		borderStyle: 'solid',
		borderRadius: 'radius.small',
		borderWidth: 'border.width',
		color: 'color.text',
		cursor: 'pointer',
	}),
	dateTagNoBorder: xcss({ cursor: 'pointer' }),
	flexContainer: xcss({ display: 'flex', alignItems: 'center', gap: 'space.050' }),
	overdue: xcss({ color: 'color.text.danger', borderColor: 'color.border.danger' }),
	dueToday: xcss({ color: 'color.text.warning', borderColor: 'color.border.warning' }),
	normalDate: xcss({ color: 'color.text', borderColor: 'color.border' }),
	dueDateIcon: xcss({ display: 'flex', alignItems: 'center' }),
};
